import React, { useMemo } from "react";
import {
  Controller,
  useWatch,
  type Control,
  type ControllerRenderProps,
  type FieldErrors,
  type FieldValues,
} from "react-hook-form";
import GenericField, { fieldTypeOptions } from "../../GenericField";
import {
  TextButton,
  IconButton,
  TextInput,
  REQUIRED_ERROR_MESSAGE,
} from "@SeemplicityDev/shared";

// RHF FieldErrors is read through a dynamic nested string/index path; this
// structural type models that traversal (numeric indices coerce to string keys).
type NestedErrors = {
  [key: string]: NestedErrors | undefined;
};

interface MappingFieldOption {
  id?: string;
  name?: string;
}

interface MappingOption {
  name?: string;
  label?: string;
  input?: string;
  options?: MappingFieldOption[];
  disabled?: boolean;
}

interface DynamicMappingFieldRowProps {
  control: Control<FieldValues>;
  errors?: FieldErrors<FieldValues>;
  registerPrefix: string;
  label?: string;
  mappingOptions: MappingOption[];
  removeRow: (index: number) => void;
  mappedFields: string[];
  updateRow: (
    index: number,
    value: { field: string | null; value: unknown }
  ) => void;
  index: number;
  value?: string;
  rowsNumber: number;
  field?: string;
  appendRow: (value: { field: null; value: null }) => void;
  disabled?: boolean;
  displayValue?: string;
  templateDisabled?: boolean;
}

// `onFocus` is not part of the standard RHF field shape but is forwarded here to
// match the original runtime behavior.
type ControllerFieldWithFocus = ControllerRenderProps<FieldValues> & {
  onFocus?: () => void;
};

const DynamicMappingFieldRow: React.FC<DynamicMappingFieldRowProps> = (
  props
) => {
  const {
    control,
    errors,
    registerPrefix,
    label,
    mappingOptions,
    removeRow,
    mappedFields,
    updateRow,
    index,
    value,
    rowsNumber,
    field,
    appendRow,
    disabled,
    displayValue,
    templateDisabled,
  } = props;

  const selectedField = useWatch({
    control,
    name: `${registerPrefix}[${index}].field`,
  });

  const currOption = useMemo(() => {
    if (!!mappingOptions && !!field) {
      return mappingOptions.find((el) => el?.name === selectedField);
    }
  }, [mappingOptions, selectedField]);

  const augmentedOptions = useMemo(() => {
    if (
      currOption?.input !== fieldTypeOptions.SELECT_QUERY ||
      !value ||
      !displayValue
    ) {
      return currOption?.options;
    }
    const opts = currOption?.options ?? [];
    if (opts.find((opt) => opt?.id === value)) {
      return opts;
    }
    return [...opts, { id: value, name: displayValue }];
  }, [currOption, value, displayValue]);

  const getFieldChoice = () => {
    return (
      <Controller
        name={`${registerPrefix}[${index}].field`}
        render={({
          field: { onChange, onBlur, value, onFocus },
        }: {
          field: ControllerFieldWithFocus;
        }) => {
          let currentOptions = mappingOptions.filter(
            (el) => !mappedFields.includes(el.name as string)
          );
          if (value) {
            const valueOption = mappingOptions.find((el) => el.name === value);
            if (valueOption) {
              currentOptions.push(valueOption);
            }
          }
          const mappedOptions = currentOptions.map((el) => {
            return {
              id: el.name,
              name: el.label,
            };
          });
          return (
            <GenericField
              onChange={(val) => {
                onChange(val);
                // GenericField's onChange emits `unknown`; the field id is a
                // string (or null when cleared) at runtime.
                updateRow(index, { field: val as string | null, value: null });
              }}
              setSingleOption={false}
              onBlur={onBlur}
              value={value}
              clearable={false}
              onFocus={onFocus}
              label={""}
              input={fieldTypeOptions.SELECT}
              placeholder={`Select a ${label}`}
              options={mappedOptions}
              name={`${registerPrefix}[${index}].field`}
              disabled={templateDisabled ?? (currOption?.disabled || disabled)}
            />
          );
        }}
        defaultValue={field}
        control={control}
      />
    );
  };

  const getFieldValue = () => {
    return selectedField && !!currOption ? (
      <Controller
        name={`${registerPrefix}[${index}].value`}
        render={({
          field: { onChange, onBlur, value },
        }: {
          field: ControllerRenderProps<FieldValues>;
        }) => {
          return (
            <GenericField
              {...props}
              {...currOption}
              options={augmentedOptions}
              classname={"dynamic-field-mapping-value"}
              onChange={onChange}
              onBlur={onBlur}
              value={value}
              control={control}
              label={""}
              size={"max"}
              placeholder={`Select a ${currOption?.label}`}
              error={
                // RHF's FieldErrors map doesn't expose this nested string/index
                // path access in its type, so read it through a structural index.
                (errors as NestedErrors | undefined)?.[
                  `${registerPrefix?.split(".")?.[0]}`
                ]?.[registerPrefix?.split(".")?.[1]]?.[index]?.value
              }
              required={true}
              disabled={templateDisabled ?? (currOption?.disabled || disabled)}
            />
          );
        }}
        defaultValue={value}
        control={control}
        rules={{ required: REQUIRED_ERROR_MESSAGE }}
      />
    ) : (
      <TextInput
        wrapperClassName={"dynamic-field-mapping-value"}
        data-testid={"dynamic-mapping-field-placeholder"}
        disabled
        noMargin
        // Original JS passed an explicit `null`; TextInput types value as
        // string | number. Preserve the runtime value via a cast.
        value={null as unknown as string | undefined}
        placeholder={`Description`}
      />
    );
  };

  return (
    <div className={"row"}>
      <div className={"dynamic-mapping-field-inputs"}>
        {getFieldChoice()}
        <div className={"dynamic-mapping-field-to"}>
          {getFieldValue()}
          {!disabled && !currOption?.disabled && (
            <IconButton
              data-testid={`remove-dynamic-mapping-field`}
              wrapperClassName={`remove-dynamic-mapping-field`}
              type="button"
              isIcon="seem-icon-bin"
              title={"Delete field"}
              onClick={() => {
                if (index === 0 && rowsNumber === 1) {
                  // updateRow(index, { field: null, value: null });
                  removeRow(index);
                  appendRow({ field: null, value: null });
                } else {
                  removeRow(index);
                }
              }}
            />
          )}
        </div>
      </div>
      <TextButton
        wrapperClassName={`add-dynamic-mapping-field ${
          index + 1 === mappingOptions.length || index + 1 !== rowsNumber
            ? "is-hidden"
            : ""
        }`}
        type={"button"}
        label={"+ Add Field"}
        buttonStyle={"textLink"}
        data-testid={"add-dynamic-mapping-field"}
        onClick={() => appendRow({ field: null, value: null })}
      />
    </div>
  );
};

export default DynamicMappingFieldRow;
